import React, { useMemo } from 'react'
import {
  Area,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { CalendarDays, MousePointerClick } from 'lucide-react'
import DemandChip from '@/components/market/DemandChip'
import { formatMoney } from '@/lib/format'
import { nightsUntil } from '@/lib/calendar-utils'
import { nightsAway } from '@/lib/plain-language'
import { MARKET_COLORS, TONE_STYLE } from '@/lib/market'
import type { MarketNight, MarketPriceDomain, MarketTone } from '@/types/market'

/**
 * "Your position": your price against the compset's lowest-to-highest band, night by night.
 *
 * The band is a RANGE area ([compsetMin, compsetMax] per night, as sent), not two stacked areas:
 * the old page stacked min on max and painted the range from zero, which put your line "inside the
 * market" on nights it sat below every competitor.
 *
 * A single full-height background Bar (one Cell per night) carries the weekend, hover and selected
 * tints. It also turns the x axis into a band scale, which is what lets the lanes under the plot
 * (NightLanes) line up column for column.
 */

export const CHART_Y_AXIS_WIDTH = 64
export const CHART_MARGIN_RIGHT = 12

export interface PositionDatum {
  date: string
  /** [compsetMin, compsetMax] verbatim, or null when no competitor is priced. */
  band: [number, number] | null
  compsetMedian: number | null
  yourPrice: number | null
  bookingCom: number | null
  /** Constant height for the background column. */
  columnBg: number
}

/** Pure reshape of the server's nights for Recharts. Every price passes through untouched. */
export function buildPositionData(nights: MarketNight[]): PositionDatum[] {
  return nights.map((n) => ({
    date: n.date,
    band: n.compsetMin == null || n.compsetMax == null ? null : [n.compsetMin, n.compsetMax],
    compsetMedian: n.compsetMedian,
    yourPrice: n.yourPrice,
    bookingCom: n.yourBookingComPrice,
    columnBg: 1,
  }))
}

/** Which axis labels to print: "Tonight", then Mondays (desktop) or the 1st and 15th (phone). */
export function tickLabel(night: MarketNight, index: number, compact: boolean): string | null {
  if (index === 0) return night.isToday ? 'Tonight' : `${night.dayOfMonth} ${night.monthLabel}`
  if (compact) {
    if (index < 10) return null
    if (night.dayOfMonth === 1) return night.monthLabel
    if (night.dayOfMonth === 15) return `15 ${night.monthLabel}`
    return null
  }
  if (index < 5) return null
  return night.weekday === 'Mon' ? `${night.dayOfMonth} ${night.monthLabel}` : null
}

interface TickProps {
  x?: number | string
  y?: number | string
  payload?: { value: string; index: number }
}

function PositionTooltip({
  active,
  label,
  nightByDate,
  currency,
  today,
  singleName,
}: {
  active?: boolean
  label?: string | number
  nightByDate: Map<string, MarketNight>
  currency: string
  today: string
  singleName: (night: MarketNight) => string | null
}) {
  if (!active || label == null) return null
  const night = nightByDate.get(String(label))
  if (!night) return null
  const money = (v: number) => formatMoney(v, currency)
  const one = singleName(night)

  return (
    <div className="w-64 rounded-lg border border-border/60 bg-white px-3 py-2.5 text-xs shadow-lg shadow-black/5">
      <div className="flex items-baseline justify-between gap-2">
        <p className="font-semibold text-foreground">{night.isToday ? `Tonight, ${night.label}` : night.label}</p>
        {!night.isToday && (
          <p className="text-[11px] text-muted-foreground">{nightsAway(nightsUntil(night.date, today))}</p>
        )}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-1">
        <DemandChip demand={night.marketDemand} label={night.marketDemandLabel} />
        {night.eventName && (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-2 py-0.5 text-[11px] font-medium text-white">
            <CalendarDays className="h-3 w-3" />
            {night.eventName}
          </span>
        )}
      </div>
      <dl className="mt-2 space-y-1 tabular-nums">
        <div className="flex justify-between gap-3">
          <dt className="flex items-center gap-1.5 text-muted-foreground">
            <span className="h-0.5 w-3 rounded-full bg-primary" />
            Priced at
          </dt>
          <dd className="font-semibold text-primary">
            {night.yourPrice == null ? 'Not priced' : money(night.yourPrice)}
          </dd>
        </div>
        {night.compsetMedian != null && (
          <div className="flex justify-between gap-3">
            <dt className="flex items-center gap-1.5 text-muted-foreground">
              <span className="w-3 border-t-[1.5px] border-dashed border-slate-500" />
              <span className="truncate">{one ?? 'Compset median'}</span>
            </dt>
            <dd className="font-medium text-foreground">{money(night.compsetMedian)}</dd>
          </div>
        )}
        {night.marketClaim && night.compsetMin != null && night.compsetMax != null && (
          <div className="flex justify-between gap-3">
            <dt className="flex items-center gap-1.5 text-muted-foreground">
              <span className="h-2 w-3 rounded-sm bg-slate-300/70" />
              Compset range
            </dt>
            <dd className="text-foreground">
              {money(night.compsetMin)} to {money(night.compsetMax)}
            </dd>
          </div>
        )}
        {night.gapLabel && (
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">Gap</dt>
            <dd className="text-foreground">{night.gapLabel}</dd>
          </div>
        )}
        {night.rankLabel && (
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">Rank</dt>
            <dd className="text-foreground">{night.rankLabel}</dd>
          </div>
        )}
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">Sold out</dt>
          <dd className="text-foreground">
            {night.competitorsSoldOut} of {night.competitorsTotal}
          </dd>
        </div>
        {night.occupancyLabel && (
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">OTB</dt>
            <dd className="text-right text-foreground">{night.occupancyLabel}</dd>
          </div>
        )}
      </dl>
      <p className="mt-2 flex items-center gap-1 border-t border-border/40 pt-1.5 text-[11px] text-muted-foreground">
        <MousePointerClick className="h-3 w-3" />
        Click to open this night
      </p>
    </div>
  )
}

export interface PositionChartProps {
  nights: MarketNight[]
  domain: MarketPriceDomain | null
  currency: string
  today: string
  selectedDate: string | null
  hoverIndex: number | null
  onHover: (index: number | null) => void
  onSelect: (date: string) => void
  attention: Map<string, MarketTone>
  /** The name to print instead of "Compset median" on a night with one competitor priced. */
  singleName: (night: MarketNight) => string | null
  compact?: boolean
  reducedMotion?: boolean
  ariaLabel: string
}

export default function PositionChart({
  nights,
  domain,
  currency,
  today,
  selectedDate,
  hoverIndex,
  onHover,
  onSelect,
  attention,
  singleName,
  compact = false,
  reducedMotion = false,
  ariaLabel,
}: PositionChartProps) {
  const data = useMemo(() => buildPositionData(nights), [nights])
  const nightByDate = useMemo(() => new Map(nights.map((n) => [n.date, n])), [nights])
  const indexByDate = useMemo(() => new Map(nights.map((n, i) => [n.date, i])), [nights])
  const selected = selectedDate ? nightByDate.get(selectedDate) : undefined
  const money = (v: number) => formatMoney(v, currency)

  const renderTick = ({ x, y, payload }: TickProps) => {
    if (payload == null || x == null || y == null) return <g />
    const night = nights[payload.index]
    if (!night) return <g />
    const cx = Number(x)
    const cy = Number(y)
    const text = tickLabel(night, payload.index, compact)
    const tone = attention.get(night.date)
    const isSelected = night.date === selectedDate
    return (
      <g>
        {tone && (
          <path
            d={`M ${cx - 3.5} ${cy + 7} L ${cx + 3.5} ${cy + 7} L ${cx} ${cy + 1.5} Z`}
            fill={TONE_STYLE[tone].tick}
          />
        )}
        {text && (
          <text
            x={cx}
            y={cy + 20}
            textAnchor={payload.index === 0 ? 'start' : 'middle'}
            fontSize={11}
            fill={isSelected ? MARKET_COLORS.you : '#64748b'}
            fontWeight={night.dayOfMonth === 1 || night.isToday ? 600 : 400}
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {text}
          </text>
        )}
      </g>
    )
  }

  const columnFill = (night: MarketNight, index: number) => {
    if (night.date === selectedDate) return { fill: MARKET_COLORS.you, opacity: 0.1 }
    if (index === hoverIndex) return { fill: MARKET_COLORS.hover, opacity: 0.7 }
    if (night.isWeekend) return { fill: MARKET_COLORS.weekend, opacity: 1 }
    return { fill: 'transparent', opacity: 0 }
  }

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      className={compact ? 'pointer-events-none select-none' : 'cursor-pointer select-none'}
      style={{ height: compact ? 180 : 280 }}
    >
      <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 800, height: compact ? 180 : 280 }}>
        <ComposedChart
          data={data}
          margin={{ top: 12, right: CHART_MARGIN_RIGHT, bottom: 0, left: 0 }}
          barCategoryGap={0}
          barGap={0}
          accessibilityLayer={false}
          onMouseMove={(state) => {
            if (compact) return
            const label = state?.activeLabel
            const index = label == null ? undefined : indexByDate.get(String(label))
            onHover(index ?? null)
          }}
          onMouseLeave={() => onHover(null)}
          onClick={(state) => {
            if (compact) return
            const label = state?.activeLabel
            if (label != null && nightByDate.has(String(label))) onSelect(String(label))
          }}
        >
          <CartesianGrid yAxisId="price" vertical={false} stroke={MARKET_COLORS.grid} />
          <XAxis
            dataKey="date"
            type="category"
            interval={0}
            axisLine={false}
            tickLine={false}
            height={compact ? 28 : 32}
            tick={renderTick}
          />
          <YAxis
            yAxisId="price"
            width={CHART_Y_AXIS_WIDTH}
            domain={domain ? [domain.min, domain.max] : ['auto', 'auto']}
            ticks={domain?.ticks}
            allowDataOverflow
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: '#64748b', style: { fontVariantNumeric: 'tabular-nums' } }}
            tickFormatter={(v: number) => money(v)}
          />
          {/* The column axis sits on the RIGHT with no width: a hidden left axis still shifts the visible
              one outwards in Recharts 3, which put the price labels outside the card. */}
          <YAxis yAxisId="bg" orientation="right" width={0} hide domain={[0, 1]} />

          <Bar yAxisId="bg" dataKey="columnBg" isAnimationActive={false} legendType="none">
            {nights.map((night, i) => {
              const c = columnFill(night, i)
              return <Cell key={night.date} fill={c.fill} fillOpacity={c.opacity} />
            })}
          </Bar>

          <Area
            yAxisId="price"
            type="linear"
            dataKey="band"
            stroke={MARKET_COLORS.bandEdge}
            strokeOpacity={0.6}
            strokeWidth={1}
            fill={MARKET_COLORS.band}
            fillOpacity={0.45}
            connectNulls={false}
            isAnimationActive={false}
            activeDot={false}
            legendType="none"
          />
          <Line
            yAxisId="price"
            type="linear"
            dataKey="compsetMedian"
            stroke={MARKET_COLORS.median}
            strokeWidth={1.5}
            strokeDasharray="4 3"
            dot={false}
            activeDot={false}
            connectNulls={false}
            isAnimationActive={false}
            legendType="none"
          />
          <Line
            yAxisId="price"
            type="linear"
            dataKey="bookingCom"
            stroke="none"
            dot={{ r: 2.5, fill: '#ffffff', stroke: MARKET_COLORS.you, strokeWidth: 1.25, strokeOpacity: 0.6 }}
            activeDot={false}
            connectNulls={false}
            isAnimationActive={false}
            legendType="none"
          />
          <Line
            yAxisId="price"
            type="linear"
            dataKey="yourPrice"
            stroke={MARKET_COLORS.you}
            strokeWidth={2.5}
            dot={false}
            activeDot={compact ? false : { r: 4, fill: MARKET_COLORS.you, stroke: '#ffffff', strokeWidth: 2 }}
            connectNulls={false}
            isAnimationActive={!reducedMotion}
            animationDuration={400}
            legendType="none"
          />
          {selected && selected.yourPrice != null && (
            <ReferenceDot
              yAxisId="price"
              x={selected.date}
              y={selected.yourPrice}
              r={5}
              fill={MARKET_COLORS.you}
              stroke="#ffffff"
              strokeWidth={2}
              ifOverflow="visible"
            />
          )}
          {!compact && (
            <Tooltip
              cursor={false}
              isAnimationActive={false}
              allowEscapeViewBox={{ x: false, y: true }}
              content={(props) => (
                <PositionTooltip
                  active={props.active}
                  label={props.label as string | number | undefined}
                  nightByDate={nightByDate}
                  currency={currency}
                  today={today}
                  singleName={singleName}
                />
              )}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}
